import React, {
  type ChangeEvent,
  type ChangeEventHandler,
  type FC,
  useState,
} from 'react'

import InputTextWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'

import './InputWithUnEditablePrefill.scss'

interface InputWithUnEditablePrefillProps {
  inputType?: string
  autoFocus?: boolean
  children?: React.ReactNode
  disableAutoCompleteAndFriends?: boolean
  id?: string
  name: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  onClear?: () => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  rounded?: boolean
  roundedLeft?: boolean
  roundedRight?: boolean
  value?: string | number
  placeholder?: string
  preFillText: string
  required?: boolean
  errorMessage?: string
  errors?: Record<string, unknown>
  disabled?: boolean
  watch?: string
  isDeclarative?: boolean
  defaultValue?: string | number
  minLength?: string | number
  maxLength?: string | number
  register?: (...args: never[]) => void
}

const InputWithUnEditablePrefill: FC<InputWithUnEditablePrefillProps> = ({
  inputType,
  autoFocus,
  children,
  disableAutoCompleteAndFriends,
  id,
  name,
  onChange,
  onClear,
  onKeyUp,
  rounded,
  roundedLeft,
  roundedRight,
  value,
  placeholder,
  preFillText,
  required,
  errorMessage,
  errors,
  disabled,
  watch,
  isDeclarative,
  defaultValue,
  minLength,
  maxLength,
  register,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [hasInputValue, setHasInputValue] = useState(!!defaultValue)

  const handleOnChange: ChangeEventHandler = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (onChange) {
      onChange(event)
    }
    if (event.target.value?.length > 0) {
      setHasInputValue(true)
    } else {
      setHasInputValue(false)
    }
  }
  return (
    <div className="m-InputWithUnEditablePrefill l-flex l-flex--stretch-children">
      {(isFocused || isHovered || hasInputValue) && (
        <div
          id="prefilledText"
          className={'m-InputWithUnEditablePrefill--PrefillText'}
        >
          {preFillText}
        </div>
      )}
      <div className="m-InputWithUnEditablePrefill__input">
        <InputTextWithFloatingLabel
          inputType={inputType}
          name={name}
          id={id}
          autoFocus={autoFocus}
          disableAutoCompleteAndFriends={disableAutoCompleteAndFriends}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyUp={onKeyUp}
          onChange={handleOnChange}
          value={value}
          onClear={onClear}
          rounded={rounded}
          roundedLeft={roundedLeft}
          roundedRight={roundedRight}
          placeholder={placeholder}
          required={required}
          errors={errors}
          errorMessage={errorMessage}
          disabled={disabled}
          watch={watch}
          isDeclarative={isDeclarative}
          defaultValue={defaultValue}
          minLength={minLength}
          maxLength={maxLength}
          register={register}
        >
          {children}
        </InputTextWithFloatingLabel>
      </div>
    </div>
  )
}

InputWithUnEditablePrefill.defaultProps = {
  inputType: 'text',
  children: null,
  id: undefined,
  disableAutoCompleteAndFriends: false,
  onKeyUp: undefined,
  onChange: undefined,
  onClear: undefined,
  value: undefined,
  autoFocus: false,
  rounded: true,
  roundedLeft: false,
  roundedRight: false,
  placeholder: undefined,
  required: undefined,
  disabled: undefined,
  errorMessage: '',
  errors: undefined,
  watch: undefined,
  isDeclarative: false,
  maxLength: undefined,
  minLength: undefined,
  register: undefined,
}

export default InputWithUnEditablePrefill
